import Loading from 'core/modules/atoms/Loading';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { StudentQuadrantData } from './types';

import QuadrantChart from './QuadrantChart/QuadrantChart';
import useQuadrantPositions from './QuadrantChart/useQuadrantPositions';
import StudentXrayDrawer from './StudentXrayDrawer/StudentXrayDrawer';
import './_intent-distribution-section.scss';
import { MOCK_INTENT_DISTRIBUTION_RESPONSE, MOCK_STUDENTS_MAP } from './mockIntentDistributionData';
import useIntentDistributionSection from './useIntentDistributionSection';
import useStudentsMap from '../../../../hooks/useStudentsMap';
import { useAnalyticsFilters } from '../../../../contexts/AnalyticsFiltersContext';

const AVATAR_SIZE = 48;

type IntentDistributionSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

function IntentDistributionSection({ allCourseIds, classValue, groupCourseMap, timePeriodValue }: IntentDistributionSectionProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const { loading: dataLoading, students: rawStudents } = useIntentDistributionSection({
    allCourseIds,
    classValue,
    groupCourseMap,
    timePeriodValue,
  });
  const { loading: studentsMapLoading, studentsMap } = useStudentsMap();
  const { isMockMode } = useAnalyticsFilters();

  const effectiveStudents = isMockMode ? MOCK_INTENT_DISTRIBUTION_RESPONSE.students : rawStudents;
  const effectiveStudentsMap = isMockMode ? MOCK_STUDENTS_MAP : studentsMap;
  const effectiveLoading = isMockMode ? false : dataLoading || studentsMapLoading;

  const studentQuadrantData: StudentQuadrantData[] = useMemo(() => {
    return effectiveStudents.map((student) => {
      let qualityScore = 0;

      if (student.intentCounts) {
        Object.values(student.intentCounts).forEach((intentScore) => {
          if (intentScore.totalScore !== null) {
            qualityScore += intentScore.totalScore;
          }
        });
      }

      const info = effectiveStudentsMap.get(student.userId);

      return {
        avatar: info?.avatar || null,
        groupName: info?.groupName || '',
        intentCounts: student.intentCounts || {},
        lastName: info?.lastName || '',
        name: info?.name || '?',
        qualityScore,
        totalMessages: student.totalMessages,
        userId: student.userId,
      };
    });
  }, [effectiveStudents, effectiveStudentsMap]);

  const [drawerStudent, setDrawerStudent] = useState<StudentQuadrantData | null>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ height: 0, width: 0 });

  const showChart = !effectiveLoading && studentQuadrantData.length > 0;

  useEffect(() => {
    const el = chartWrapperRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const { height, width } = entries[0].contentRect;
      setChartSize({ height, width });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [showChart]);

  const handleStudentClick = useCallback((student: StudentQuadrantData) => {
    setDrawerStudent(student);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerStudent(null);
  }, []);

  const { clusters, maxQualityScore, xScale, yScale } = useQuadrantPositions({
    avatarSize: AVATAR_SIZE,
    chartHeight: chartSize.height,
    chartWidth: chartSize.width,
    students: studentQuadrantData,
  });

  return (
    <div className="intent-distribution-section">
      <div className="intent-distribution-section__header">
        <div className="intent-distribution-section__header-text">
          <h2 className="intent-distribution-section__title">{t('chatbot:ai_analytics_xray_title')}</h2>
          <p className="intent-distribution-section__subtitle">{t('chatbot:ai_analytics_xray_subtitle')}</p>
        </div>
      </div>

      {effectiveLoading ? (
        <div className="intent-distribution-section__loading">
          <Loading />
        </div>
      ) : studentQuadrantData.length === 0 ? (
        <p className="intent-distribution-section__empty">{t('chatbot:ai_analytics_xray_empty')}</p>
      ) : (
        <div className="intent-distribution-section__chart-wrapper" ref={chartWrapperRef}>
          <QuadrantChart clusters={clusters} loading={false} onStudentClick={handleStudentClick} />
        </div>
      )}
      <StudentXrayDrawer
        maxQualityScore={maxQualityScore}
        onClose={handleDrawerClose}
        open={drawerStudent !== null}
        qualityScaleType={xScale}
        student={drawerStudent}
      />
    </div>
  );
}

export default IntentDistributionSection;
